import React, { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { v4 as uuidv4 } from 'uuid';
import { TransferProgressEvent } from '@aws-amplify/storage';
import {
  S3Level,
  fileType,
  Image,
  S3Upload,
  S3UploadType,
  formatFileSize,
} from '@admiin-com/ds-common';
import { imageResizer } from '@admiin-com/ds-web';
import {
  deleteFromS3Storage,
  getFromS3Storage,
  uploadToS3Storage,
} from '@admiin-com/ds-amplify';

export const sanitizeFileName = (name: string) => name.replace(/\s+/g, '_');

/**
 * Extracts the original filename from an S3 key.
 * S3 keys are formatted as: {uuid}_{sanitized_filename}
 * This function returns everything after the first underscore.
 *
 * @param key - The S3 key (e.g., "abc123_Cover_Sheet_for_Admiin.pdf")
 * @returns The original filename (e.g., "Cover_Sheet_for_Admiin.pdf").
 *          If no underscore is found (invalid format), returns the original key as-is.
 */
export const getFileNameFromKey = (key: string): string => {
  const firstUnderscoreIndex = key.indexOf('_');
  if (firstUnderscoreIndex === -1) {
    // No underscore found - this likely means an older key format or direct filename
    return key;
  }
  return key.substring(firstUnderscoreIndex + 1);
};

interface UseS3MediaUploadProps {
  validFileTypes: string[];
  maxFiles?: number;
  level?: S3Level;
  inputAccept: string;
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  alwaysKeepResolution?: boolean;
  useWebWorker?: boolean;
  onImageUpload?: (img: Image, file: File) => void;
  onUploaded?: (img: S3Upload[]) => void;
  onDropped?: (files: FileList) => void;
  includeNameInKey?: boolean;
}

export const useS3MediaUpload = ({
  validFileTypes,
  maxFiles = 1,
  level = 'private',
  maxSizeMB,
  inputAccept,
  maxWidthOrHeight = 1920,
  alwaysKeepResolution = true,
  useWebWorker = true,
  onImageUpload,
  onUploaded,
  onDropped,
  includeNameInKey,
}: UseS3MediaUploadProps) => {
  const [identityId, setIdentityId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [uploadedFiles, setFiles] = useState<
    {
      image?: S3Upload;
      name: string;
      size: string;
      id: string;
      progress: number;
      type: 'UPLOAD' | 'EMAIL';
    }[]
  >([]);

  useEffect(() => {
    const getIdentityId = async () => {
      const data = await fetchAuthSession();
      setIdentityId(data?.identityId ?? '');
    };
    if (level !== 'public') {
      getIdentityId();
    }
  }, [level]);
  const onFileDrop = async (files: FileList) => {
    let currentIdentityId = identityId;
    if (!currentIdentityId && level !== 'public') {
      try {
        const data = await fetchAuthSession();
        currentIdentityId = data?.identityId ?? null;
        if (currentIdentityId) {
          setIdentityId(currentIdentityId);
        }
      } catch (err) {
        console.log('Failed to fetch identityId on retry:', err);
      }

      if (!currentIdentityId) {
        setError('Error uploading media. Please re-open screen');
        return;
      }
    }
    if (onDropped) onDropped(files);
    const totalFiles = uploadedFiles.length + files.length;
    const uploadedFileSize = uploadedFiles.length;
    const sizeString = (file: File) => formatFileSize(file.size);

    if (totalFiles > maxFiles) {
      setError(`You can only upload a maximum of ${maxFiles} files.`);
      return;
    } else {
      setError('');
    }

    const getFileName = (file: File) => {
      if (!includeNameInKey) return `${uuidv4()}.${fileType(file.name)}`;
      else return `${uuidv4()}_${sanitizeFileName(file.name)}`;
    };
    // Initialize uploadedFiles state with all files

    const newFiles = Array.from(files).map((file) => ({
      name: file.name,
      size: sizeString(file),
      id: getFileName(file),
      progress: 0,
      type: 'UPLOAD' as const,
    }));

    setFiles(uploadedFiles.concat(...newFiles));

    // Create array of upload promises
    const uploadPromises = Array.from(files).map(async (file, index) => {
      const newFileName = newFiles[index].id;
      let processedFile;
      if (maxSizeMB && file.type.startsWith('image/')) {
        const resizeOptions = {
          maxSizeMB,
          alwaysKeepResolution,
          maxWidthOrHeight,
          useWebWorker,
        };
        processedFile = await imageResizer(file, resizeOptions);
      } else {
        processedFile = file;
      }

      const attemptUpload = async () => {
        await uploadToS3Storage(
          {
            key: newFileName,
            contentType: file.type,
            file: processedFile,
            level,
          },
          (data: TransferProgressEvent) => {
            const progress =
              (data.transferredBytes / (data.totalBytes ?? 0)) * 100;
            setFiles((prev) =>
              prev.map((f, idx) =>
                idx === index + uploadedFileSize ? { ...f, progress } : f
              )
            );
          }
        );

        const key = newFileName;
        const url = await getFromS3Storage({
          key,
          identityId: currentIdentityId,
          level,
        });
        const image = {
          key,
          src: url,
          level,
          identityId: currentIdentityId,
        };

        let type: S3UploadType = 'FILE';
        if (file.type.startsWith('image')) type = 'IMAGE';
        if (file.type.startsWith('video')) type = 'VIDEO';
        if (file.type.startsWith('pdf')) type = 'PDF';

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === index + uploadedFileSize
              ? { ...f, image: { ...image, type }, progress: 0 }
              : f
          )
        );

        onImageUpload && onImageUpload(image, processedFile);

        return {
          ...image,
          name: file.name,
          type,
          size: sizeString(file),
        };
      };

      try {
        return await attemptUpload();
      } catch (err) {
        console.log('Upload attempt 1 failed, retrying:', err);
        try {
          return await attemptUpload();
        } catch (retryErr) {
          console.log('ERROR upload file S3MediaDragDrop:', retryErr);
          return null;
        }
      }
    });

    // Wait for all uploads to complete
    const results = await Promise.all(uploadPromises);
    const successfulUploads = results.filter(
      (result): result is NonNullable<typeof result> => result !== null
    );
    const totalUploadedFiles: S3Upload[] = uploadedFiles
      .filter((file) => file.image)
      .map((file) => ({
        id: file.id,
        key: file.image?.key ?? '',
        name: file.name,
        size: file.size,
        type: file.image?.type ?? 'FILE',
        level: file.image?.level ?? 'private',
        src: file.image?.src ?? '',
        identityId: file.image?.identityId ?? null,
      }))
      .concat(
        successfulUploads.map((upload) => ({
          id: upload.key,
          ...upload,
        }))
      );
    onUploaded && onUploaded(totalUploadedFiles);
  };

  const remove = async (key: string) => {
    await deleteFromS3Storage(key, level);
    const images: S3Upload[] = [];
    for (const f of uploadedFiles) {
      if (f.image && f.id !== key) {
        images.push(f.image);
      }
    }
    onUploaded && onUploaded(images);
    setFiles((prev) => prev.filter((f) => f.id !== key));
  };

  const inputRef = React.useRef<HTMLInputElement>(null);

  const onSelect = () => {
    if (!inputRef.current?.files) {
      setError('Invalid upload');
      return;
    } else {
      if (inputRef.current?.files.length > maxFiles) {
        setError(`Only ${maxFiles} file can be uploaded`);
      } else if (inputRef.current?.files.length === 1) {
        handleFiles(inputRef.current.files);
      }
      inputRef.current.value = '';
    }
  };

  const handleFiles = (files: FileList) => {
    let isError = false;
    for (let i = 0; i < files.length; i++) {
      const invalidFileType = validFileTypes.indexOf(files[i].type) === -1;
      if (invalidFileType) {
        setError(`Invalid file type ${files[i].type}`);
        isError = true;
        break;
      }
    }

    if (!isError) {
      setError('');
      onFileDrop(files);
    }
  };

  const uploadFile = () => {
    inputRef.current?.click();
  };

  const inputElement = (
    <input
      ref={inputRef}
      type="file"
      accept={inputAccept}
      multiple={maxFiles > 1}
      onChange={onSelect}
      style={{ display: 'none' }}
    />
  );

  return {
    identityId,
    error,
    uploadedFiles,
    onFileDrop,
    remove,
    inputElement,
    uploadFile,
  };
};
